import { useRef } from 'react';

const useBodyScrollLock = () => {
  const originalPaddingRight = useRef<string | null>(null);
  const originalTop = useRef<number>(0);
  const isLocked = useRef<boolean>(false);

  const disableScroll = () => {
    if (typeof window === 'undefined') return;

    // Check if body already has overflow hidden
    if (window.getComputedStyle(document.body).overflow === 'hidden') {
      isLocked.current = false; // Indicate that the hook didn't lock the scroll
      return;
    }

    // Save the current scroll position
    originalTop.current = window.scrollY;

    // Add padding to prevent layout shift due to scrollbar disappearance
    const scrollBarGap = window.innerWidth - document.documentElement.clientWidth;
    originalPaddingRight.current = document.body.style.paddingRight;
    if (scrollBarGap > 0) {
      document.body.style.paddingRight = `${scrollBarGap}px`;
    }

    // Lock scroll using `position: fixed` and save the scroll offset in `top`
    document.body.style.position = 'fixed';
    document.body.style.top = `-${originalTop.current}px`;
    document.body.style.left = '0';
    document.body.style.width = '100%';

    isLocked.current = true;
  };

  const enableScroll = () => {
    if (typeof window === 'undefined' || !isLocked.current) return;

    // Get the original scroll position from the `top` property
    const scrollY = Math.abs(parseInt(document.body.style.top || '0', 10));

    // Temporarily disable scroll animation
    const originalScrollBehavior = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';

    // Restore original styles
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.width = '';
    if (originalPaddingRight.current !== null) {
      document.body.style.paddingRight = originalPaddingRight.current;
    }

    // Restore the scroll position without animation
    window.scrollTo(0, scrollY);

    // Restore the original scroll-behavior
    document.documentElement.style.scrollBehavior = originalScrollBehavior || '';

    isLocked.current = false;
  };

  return { disableScroll, enableScroll };
};

export default useBodyScrollLock;
